package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.shared.Role
import dev.lingvo.shared.ScreenshotQuotaDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.api.screenshotUsage
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.WorkspaceSelection
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.awaitCancellation
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
fun ScreenshotUsageMeter(slug: String, platform: StorePlatform, refresh: Int) {
    val subject = liveProjectRoute(slug)?.authoritySubject(Session.organizations)
    var retry by remember(subject, platform) { mutableStateOf(0) }
    val loaded = loadReloadable(subject, platform, reloadToken = 31 * refresh + retry) {
        val live = subject ?: awaitCancellation()
        requestProjectResource(live.action, slug, Session.organizations) { ApiClient.screenshotUsage(slug, platform) }
    }
    loaded.refreshError?.let { AsyncFailureState(it, subject = t("app.projects.slug.screenshots.usage_meter.screenshot_storage_usage", "screenshot storage usage"), onRetry = { retry++ }) }
    when (val state = loaded.state) {
        Async.Loading -> LoadingState(rows = 1)
        is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.usage_meter.screenshot_storage_usage", "screenshot storage usage"), onRetry = { retry++ })
        is Async.Success -> UsageMeter(state.value, slug, ScreenshotsAreaState(platform))
    }
}

/** Human units retain over-budget usage while the meter itself stays within its accessible range. */
@Composable
internal fun UsageMeter(
    quota: ScreenshotQuotaDto,
    slug: String? = null,
    params: ScreenshotsAreaState = ScreenshotsAreaState(),
    currentRoute: () -> VerifiedProjectRoute? = { slug?.let(::liveProjectRoute) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    onNavigate: ((String) -> Unit)? = null,
) {
    Column(Modifier.gap(Space.SM).styleModifier { property("width", "100%"); property("min-width", "0"); property("box-sizing", "border-box") }) {
        LMuted(tf("shots.usage.captures", "Screenshot storage · {count} captures", "count" to quota.captures))
        UsageByteMeter(t("app.projects.slug.screenshots.usage_meter.project_screenshot_storage", "Project screenshot storage"), quota.projectUsedBytes, quota.projectLimitBytes)
        UsageByteMeter(t("app.projects.slug.screenshots.usage_meter.workspace_screenshot_storage", "Workspace screenshot storage"), quota.workspaceUsedBytes, quota.workspaceLimitBytes)
        if (screenshotStorageFull(quota)) {
            LAlert(t("app.projects.slug.screenshots.usage_meter.screenshot_storage_is_full_delete_images", "Screenshot storage is full. Delete images you no longer need before adding captures or composing renders."), variant = LAlertVariant.WARNING)
            if (slug != null) ScreenshotStorageRecovery(slug, params, currentRoute, currentMemberships, onNavigate)
        }
    }
}

/** Recovery belongs to the verified project, never the strongest or last selected workspace. */
@Composable
private fun ScreenshotStorageRecovery(
    slug: String,
    params: ScreenshotsAreaState,
    currentRoute: () -> VerifiedProjectRoute?,
    currentMemberships: () -> List<WorkspaceDto>,
    onNavigate: ((String) -> Unit)?,
) {
    val route = currentRoute()?.takeIf { it.project.slug == slug }
    val claim = ProjectActionClaim.capture(route, currentMemberships(), ProjectPrivilege.READ) ?: return
    val owner = claim.subject.role == Role.OWNER
    val canCleanup = claim.subject.role != Role.VIEWER
    val router = if (onNavigate == null) rememberPageContext().router else null
    val navigate: (String) -> Unit = onNavigate ?: { router?.tryRoutingTo(it); Unit }
    val palette = ColorMode.current.toSitePalette()
    if (!owner) {
        LMuted(t("app.projects.slug.screenshots.usage_meter.ask_a_workspace_owner_to_review", "Ask a workspace owner to review plans for more screenshot storage. Upgrading does not remove the byte limits."))
    }
    if (!canCleanup) {
        LMuted(t("app.projects.slug.screenshots.usage_meter.to_free_storage_ask_an_owner", "To free storage, ask an owner, admin or translator to remove images they no longer need."))
    }
    Div(Modifier.styleModifier {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", Space.SM.toString())
        property("min-width", "0")
    }.toAttrs()) {
        val linkModifier = Modifier.styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("min-height", "40px")
            property("max-width", "100%")
            property("box-sizing", "border-box")
            property("padding", "8px 12px")
            property("border", "1px solid ${palette.borderStrong}")
            property("color", palette.accent.toString())
            property("overflow-wrap", "anywhere")
        }
        if (owner) {
            val plans = "/app/settings/billing/plans"
            A(href = plans, attrs = linkModifier.toAttrs {
                attr("data-storage-plans", "")
                onClick { event ->
                    // Recheck before changing the selection: old DOM can survive until recomposition.
                    if (claim.remainsAuthorized(currentRoute(), currentMemberships())) {
                        WorkspaceSelection.select(claim.workspaceKey.workspaceSlug)
                        if (event.button.toInt() == 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
                            event.preventDefault()
                            navigate(plans)
                        }
                    } else event.preventDefault()
                }
            }) { Text(t("app.projects.slug.screenshots.usage_meter.review_plans", "Review plans")) }
        }
        if (canCleanup) {
            val cleanup = screenshotsPath(slug, ScreenshotsTab.PLAN, params.copy(cell = null))
            A(href = cleanup, attrs = linkModifier.toAttrs {
                attr("data-storage-cleanup", "")
                onClick { event ->
                    if (!claim.remainsAuthorized(currentRoute(), currentMemberships())) {
                        event.preventDefault()
                    } else if (event.button.toInt() == 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
                        event.preventDefault()
                        navigate(cleanup)
                    }
                }
            }) { Text(t("app.projects.slug.screenshots.usage_meter.review_captures_for_cleanup", "Review captures for cleanup")) }
        }
    }
}

@Composable
private fun UsageByteMeter(label: String, used: Long, limit: Long) {
    val palette = ColorMode.current.toSitePalette()
    val percent = screenshotUsagePercent(used, limit)
    val sentence = tf("shots.usage.bytes", "{label}: {used} of {limit}", "label" to label, "used" to screenshotStorageSize(used), "limit" to screenshotStorageSize(limit))
    Div(Modifier.styleModifier { property("width", "100%"); property("min-width", "0") }.toAttrs {
        attr("role", "meter"); attr("aria-label", label); attr("aria-valuemin", "0"); attr("aria-valuemax", "100")
        attr("aria-valuenow", screenshotUsagePercent(used, limit).toString()); attr("aria-valuetext", sentence)
    }) {
        LMuted(sentence)
        Div(Modifier.styleModifier { property("width", "100%"); property("height", Space.MD.toString()); property("border", "1px solid ${palette.borderStrong}"); property("box-sizing", "border-box"); property("background", palette.surfaceSunken.toString()); property("overflow", "hidden") }.toAttrs { attr("aria-hidden", "true") }) {
            Div(Modifier.styleModifier { property("height", "100%"); property("width", "$percent%"); if (used > 0) property("min-width", "2px"); property("background", (if (used >= limit) palette.danger else palette.accent).toString()) }.toAttrs())
        }
    }
}
